import { CommonModule } from '@angular/common';
import { Component, HostListener, signal, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import {
  NgbDropdownModule,
  NgbModal,
  NgbPopoverModule,
  NgbTooltipModule,
} from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, filter, startWith, Subject, takeUntil } from 'rxjs';
import { ChatStateService } from '../../../components/agent/chat/chat.service';
import { LogoComponent } from '../../../shared/common/logo';
import { ConfirmationPopupComponent } from '../../../shared/common/popups/confirmation-popup/confirmation-popup.component';
import { AuthService } from '../../../shared/services/auth.service';
import { CommonDataSharingService } from '../../../shared/services/common-data-sharing.service';
import { ModalService } from '../../../shared/services/modal.service';
import { ApiCallsService } from '../../../shared/services/network/api-calls.service';
import { SmoothScrollSidebarDirective } from '../../../utils/directives/smoothScrollSidebar.directive';
import { api } from '../../../utils/endpoints';
import { SafeHtmlPipe } from '../../../utils/pipes/safe-html.pipe';
import { confirmationMessages } from '../../../utils/popup-messages';
import { ICONS } from '../../../utils/svg-icons';
import { SidebarService } from '../../services/sidebar.service';

@Component({
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    RouterLink,
    SafeHtmlPipe,
    NgbDropdownModule,
    NgbTooltipModule,
    FormsModule,
    NgbPopoverModule,
    SmoothScrollSidebarDirective,
    LogoComponent,
  ],
  selector: 'app-agent-sidebar',
  templateUrl: './agent-sidebar.html',
  styleUrl: './agent-sidebar.scss',
})
export class AgentSidebar {
  public menuItems = signal<any>([]);
  collapse = true;
  currentUrl = signal<any>(null);
  ICONS = ICONS;
  id_details: any;
  limit = signal(10);
  search = signal<any>(null);
  user: any;
  EditContent: any;
  editId: any;
  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  isMobile = signal(false);
  sessionActions = [
    {
      label: 'Rename',
      icon: ICONS.renameIcon,
      handler: (data: any) => {
        this.editId = data?.id;
        this.EditContent = data?.title;
      },
    },
    {
      label: 'Delete',
      icon: ICONS.deleteIcon,
      handler: (data: any, index: number) => this.deleteSession(data.id),
    },
  ];
  isLoading = signal(false);
  filteredChats = signal<any>([]);
  agentDetails = signal<any>({});
  private collapseSidebar: boolean = window.innerWidth < 1025;
  private lastWidth = window.innerWidth;

  constructor(
    private readonly _ngbModal: NgbModal,
    private readonly _router: Router,
    private readonly _route: ActivatedRoute,
    private readonly _apiCalls: ApiCallsService,
    private readonly _common: CommonDataSharingService,
    private readonly _toast: ToastrService,
    private readonly _auth: AuthService,
    private readonly _modalService: ModalService,
    private readonly chatState: ChatStateService,
    private readonly sidebarService: SidebarService,
  ) {
    this._getList();
    this._router.events
      .pipe(filter((event: any) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.sidebarClose();
      });

    this.searchSubject.pipe(debounceTime(700), takeUntil(this.destroy$)).subscribe((search) => {
      if (search !== '') {
        this.filterData(search);
      } else this.clearSearch();
    });
    this.getProfile();
    this.chatState.footerItems$.subscribe((items: any) => {
      if (items.length == 2) {
        this._getList();
      }
    });
    this.sidebarService.deleteSession$.subscribe((id) => {
      this._getList();
    });
    this._router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        // read :id from deepest activated route
        let r = this._route;
        while (r.firstChild) r = r.firstChild;
        let chatId = r.snapshot.paramMap.get('id'); // will change on navigation
        this.currentUrl.set(chatId ?? '');
      });
  }
  watchRouteParamId(): void {
    this._route.firstChild?.params
      .pipe(startWith(this._route.snapshot.params))
      .subscribe((params) => {
        const id = params['id'];
        this.currentUrl.set(id ?? '');
      });
  }

  getInitials(name: string): string {
    if (!name) return '';

    const words = name.trim().split(/\s+/);
    const initials = words.slice(0, 2).map((word) => word[0].toUpperCase());
    return initials.join('');
  }

  searchInput(event: any) {
    const searchValue = event.target.value.trim().toLowerCase();
    this.searchSubject.next(searchValue);
  }

  toggleSidebar(event: any) {
    this.collapse = !this.collapse;
    document.querySelector('body')?.classList.toggle('sidenav-toggled');
  }

  toggleNavActive(item: any, i: any): void {
    this.menuItems().map((x: any, i: any) => {
      if (x.id != item.id) {
        this.menuItems()[i].selected = false;
      }
    });

    this.menuItems()[i].selected = true;
    if (item.id != null) {
      this._router.navigate(['/agent/chat', item.id]);
      this.currentUrl.set(item.id);
    } else {
      this._router.navigate(['/agent/chat']);
      this.currentUrl.set('');
    }
    if (this.editId != item.id) {
      this.editId = '';
      this.EditContent = '';
    }
  }

  toggleSidebarMobile(value: any) {
    this.collapse = !this.collapse;
    document.querySelector('body')?.classList?.toggle('sidenavToggledMobile');
    this.currentUrl.set(value ?? '');
  }

  deleteSession(sessionId: any) {
    const isCorrent = this.currentUrl() == sessionId;
    let modalRef = this._ngbModal.open(ConfirmationPopupComponent, {
      scrollable: false,
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'small confirmationPopup',
    });
    modalRef.componentInstance.confirmationMessage = confirmationMessages.deleteSesion;
    modalRef.result.then((res: any) => {
      if (res) {
        this._apiCalls.delete(`${api._deleteChat}/${sessionId}`, {}).subscribe({
          next: (res: any) => {
            if (isCorrent) {
              this._router.navigate(['/agent/chat']);
              this.currentUrl.set('');
            }
            this._getList();
            this._toast.success('Session has been deleted successfully');
          },
          error: (error: any) => {
            console.log(error);
          },
        });
      }
    });
  }

  showSubmit(value: any) {
    if (value == '' || value == null || value == undefined) {
      return false;
    }
    const hasWhitespace = (value as string)?.trim().length === 0;

    return !hasWhitespace;
  }
  updateName(i: any) {
    let data = { chat_id: this.menuItems()[i].id, title: this.EditContent };
    this._apiCalls.post(api._updateChatTitle, data).subscribe({
      next: (res: any) => {
        this._getList();
        this._toast.success('Session has been updated successfully');
      },
      error: (error: any) => {
        console.log(error);
      },
    });
    this.editId = '';
    this.EditContent = '';
  }

  filterData(search: string) {
    const query = this.search().toLowerCase();
    const chats = this.menuItems();

    if (query) {
      const filtered = chats.filter((item: any) => item.title.toLowerCase().includes(query));
      this.filteredChats.set(filtered);
    } else {
      this.filteredChats.set([...chats]);
    }
  }

  clearSearch() {
    this.search.set('');
    this.filteredChats.set([...this.menuItems()]);
  }

  timeAgo(date: any) {
    return this._common.timeAgo(date);
  }
  sidebarClose() {
    if (this.collapseSidebar) {
      const bodyElement = document.querySelector('body');
      if (bodyElement?.classList.contains('sidenav-toggled')) {
        bodyElement.classList.remove('sidenav-toggled');
      }
      if (bodyElement?.classList.contains('sidenavToggledMobile')) {
        bodyElement.classList.remove('sidenavToggledMobile');
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    const currentWidth = window.innerWidth;
    // ❌ height change (keyboard open) → ignore
    if (currentWidth === this.lastWidth) {
      return;
    }

    this.lastWidth = currentWidth;

    this.collapseSidebar = currentWidth < 1025;
    this.sidebarClose();
  }
  _getList(): void {
    this.isLoading.set(true);
    this._apiCalls.getDataWithoutLoader(api._chats, {}).subscribe({
      next: (res: any) => {
        const withTimeAgo = res.data.chats?.map((chat: any, index: number) => ({
          ...chat,
          timeAgo: this.timeAgo(chat.last_message_time),
        }));
        this.menuItems.set(withTimeAgo);

        this.filteredChats.set(withTimeAgo);
        this.isLoading.set(false);
      },
      error: (error: any) => {
        console.log(error);

        this.isLoading.set(false);
      },
    });
  }
  logout() {
    const modalRef = this._modalService.open(ConfirmationPopupComponent, {
      backdrop: 'static',
      centered: true,
      keyboard: false,
      size: 'small confirmationPopup',
    });
    modalRef.componentInstance.confirmationMessage = confirmationMessages.logout;
    modalRef.result.then((res: any) => {
      if (res) {
        this._auth.signOut();
      }
    });
  }
  private getProfile(): void {
    this._auth.getUserProfile().subscribe({
      next: (res: any) => {
        this.user = res;
      },
      error: (err: any) => {},
    });
  }
  colabe(data: any) {
    return this.collapse && data.title.length > 17 ? false : !this.collapse;
  }
}
